/**
 * A review issue in Needs you (WS-22): worked where it lives — beside its
 * paragraph in the draft — so here it says what it is and opens there
 * (Enter).
 */

import { Badge, Button, Group, Stack, Text } from '@mantine/core';

import type { ReviewIssue } from '@/types/workbench';
import { words } from '../format';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { Kbd } from './common';
import classes from '../NeedsYou.module.css';

interface IssuePointerProps {
  issue: ReviewIssue;
  /** Release refuses while it's open. */
  holdsRelease: boolean;
  onOpen: () => void;
  keyboard: boolean;
}

export function IssuePointer({
  issue,
  holdsRelease,
  onOpen,
  keyboard,
}: Readonly<IssuePointerProps>) {
  useWorkbenchKeys({ Enter: onOpen }, keyboard);
  return (
    <div className={classes.detailBody} data-testid="pane-issue">
      <Stack gap="sm">
        <Group gap={6} wrap="wrap">
          <Badge size="sm" variant="light" color="orange" style={{ textTransform: 'none' }}>
            Draft review
          </Badge>
          <Badge
            size="xs"
            variant="light"
            color={issue.severity === 'error' ? 'red' : 'yellow'}
            style={{ textTransform: 'none' }}
          >
            {issue.severity === 'error' ? 'Error' : 'Warning'}
          </Badge>
          <Text size="xs" c="dimmed">
            {issue.source === 'layout' ? 'Layout' : 'The reviewer'} · {words(issue.rule)}
            {issue.page !== null ? ` · page ${issue.page}` : ''}
          </Text>
          {holdsRelease && (
            <Badge size="xs" variant="light" color="red" style={{ textTransform: 'none' }}>
              release waits on it
            </Badge>
          )}
        </Group>
        <Text size="sm">{issue.message}</Text>
        {issue.quote && (
          <Text size="xs" fs="italic" c="dimmed">
            “{issue.quote}”
          </Text>
        )}
        <Text size="xs" c="dimmed">
          The draft&apos;s issues are worked beside their paragraphs, in the Document.
        </Text>
        <Button
          size="xs"
          onClick={onOpen}
          style={{ alignSelf: 'flex-start' }}
          data-testid="open-in-draft"
        >
          Open it in the draft
          <Kbd>Enter</Kbd>
        </Button>
      </Stack>
    </div>
  );
}
